import { useState } from 'react';
import type { KeywordOverride, PageKeyword as Keyword } from '../../../lib/aiJobSummary';
import { Badge, Button, Input } from '../../ui';
import { OpCard } from './SectionHeading';

const SOURCE: Record<Keyword['source'], string> = {
  given: 'given with the job',
  search_console: 'picked from Search Console',
  ahrefs: 'picked by Ahrefs volume',
};
const faint: React.CSSProperties = { fontSize: 'var(--text-xs)', color: 'var(--text-faint)' };

/**
 * Page Build jobs: the search the page is built to win. Section agents write toward it and the SEO review checks it
 * (density 0.5–1%, title, H1, opening). Editable before approval; the change lands in keyword_override and wins.
 */
export default function PageKeyword({
  keyword,
  override,
  readOnly,
  onSave,
}: {
  keyword?: Keyword;
  override?: KeywordOverride | null;
  readOnly: boolean;
  onSave?: (keyword: { primary: string; secondary: string[] } | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const shown = override ? { primary: override.primary, secondary: override.secondary } : keyword ? { primary: keyword.primary, secondary: keyword.secondary ?? [] } : null;
  const canEdit = !readOnly && !!onSave;

  const save = async (value: { primary: string; secondary: string[] } | null) => {
    if (!onSave) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(value);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the keyword.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <OpCard tone="raised">
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span className="t-eyebrow" style={{ color: 'var(--text-faint)' }}>
          Target search
        </span>
        {editing ? null : shown ? (
          <>
            <span style={{ fontSize: 'var(--text-md, var(--text-sm))', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'], color: 'var(--text-body)' }}>“{shown.primary}”</span>
            {keyword?.volume != null && !override ? (
              <span className="t-num" style={faint}>
                {keyword.volume.toLocaleString('en-US')}/mo
              </span>
            ) : null}
            {override ? <Badge tone="accent">changed</Badge> : keyword ? <Badge tone="neutral">{SOURCE[keyword.source]}</Badge> : null}
          </>
        ) : (
          <span style={faint}>None yet: the spec picks one</span>
        )}
        <span style={{ flex: 1 }} />
        {canEdit && !editing ? (
          <>
            {override ? (
              <Button size="sm" variant="ghost" icon="rotate-ccw" onClick={() => void save(null)} disabled={busy}>
                Agent’s pick
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              icon="pencil"
              onClick={() => {
                setPrimary(shown?.primary ?? '');
                setSecondary((shown?.secondary ?? []).join(', '));
                setEditing(true);
              }}
            >
              Edit
            </Button>
          </>
        ) : null}
      </div>
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
          <Input fieldSize="sm" value={primary} onChange={(e) => setPrimary(e.target.value)} placeholder="The search this page is built to win" />
          <Input fieldSize="sm" value={secondary} onChange={(e) => setSecondary(e.target.value)} placeholder="Secondary searches, comma separated (optional)" />
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button size="sm" disabled={busy || !primary.trim()} onClick={() => void save({ primary, secondary: secondary.split(',') })}>
              Save keyword
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      ) : shown ? (
        <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {shown.secondary.length ? <span style={faint}>Also: {shown.secondary.join(' · ')}</span> : null}
          {keyword?.why && !override ? <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{keyword.why}</span> : null}
          <span style={faint}>Section agents write toward it; the SEO review after the build holds it to 0.5–1% of the words and checks the title, H1 and opening.</span>
        </div>
      ) : null}
      {error ? <p style={{ margin: '8px 0 0', fontSize: 'var(--text-sm)', color: 'var(--danger-400)' }}>{error}</p> : null}
    </OpCard>
  );
}
